import React, { useEffect, useMemo, useState } from 'react'
import ReactModal from 'react-modal'
import ReactQuill from 'react-quill'
import 'react-quill/dist/quill.snow.css'
import { useMutation, useQuery } from '@apollo/client'
import { toast } from 'react-toastify'
import { GET_USER_EMAIL_SIGNATURE } from '../../../../graphql/query'
import { SAVE_USER_EMAIL_SIGNATURE } from '../../../../graphql/mutation'
import { isBlankEmailSignatureHtml } from '../../../../utils/emailSignatureUtils'
import './AddSignatureModal.scss'

interface Props {
  isOpen: boolean
  onClose: () => void
  onSaved?: () => void
}

const modalStyles: ReactModal.Styles = {
  overlay: { zIndex: 1060, backgroundColor: 'rgba(15, 23, 42, 0.45)' },
  content: {
    top: '50%',
    left: '50%',
    right: 'auto',
    bottom: 'auto',
    transform: 'translate(-50%, -50%)',
    width: 'min(640px, 94vw)',
    border: 'none',
    borderRadius: '14px',
    padding: '1.75rem',
    boxShadow: '0 20px 50px rgba(0,0,0,0.15)'
  }
}

const formBuilderHeaders = {
  headers: { 'access-control-allow-methods': 'Form-Builder' }
}

const EMPTY_SIGNATURE = ''

const AddSignatureModal: React.FC<Props> = ({ isOpen, onClose, onSaved }) => {
  const [editorHtml, setEditorHtml] = useState(EMPTY_SIGNATURE)
  const [saving, setSaving] = useState(false)

  const { data, refetch } = useQuery(GET_USER_EMAIL_SIGNATURE, {
    skip: !isOpen,
    context: formBuilderHeaders,
    fetchPolicy: 'network-only'
  })

  const [saveSignature] = useMutation(SAVE_USER_EMAIL_SIGNATURE, {
    context: formBuilderHeaders
  })

  const existingSignatureHtml = data?.getUserEmailSignature?.signatureHtml || ''
  const hasSignature = Boolean(data?.getUserEmailSignature?.hasSignature)

  useEffect(() => {
    if (isOpen) {
      refetch().then((result) => {
        setEditorHtml(result.data?.getUserEmailSignature?.signatureHtml || EMPTY_SIGNATURE)
      })
    }
  }, [isOpen, refetch])

  const modules = useMemo(
    () => ({
      toolbar: [
        ['bold', 'italic', 'underline'],
        [{ list: 'ordered' }, { list: 'bullet' }],
        ['link'],
        [{ color: [] }],
        ['clean']
      ]
    }),
    []
  )

  const formats = useMemo(
    () => ['bold', 'italic', 'underline', 'list', 'bullet', 'link', 'color'],
    []
  )

  const handleSave = async () => {
    if (isBlankEmailSignatureHtml(editorHtml)) {
      toast.error('Please add your email signature before saving')
      return
    }

    setSaving(true)
    try {
      const { data: saveData } = await saveSignature({
        variables: { input: { signatureHtml: editorHtml } }
      })

      if (!saveData?.saveUserEmailSignature?.hasSignature) {
        toast.error('Failed to save signature')
        return
      }

      toast.success('Email signature saved successfully')
      onSaved?.()
      onClose()
    } catch (err: any) {
      toast.error(err?.message || 'Failed to save signature')
    } finally {
      setSaving(false)
    }
  }

  return (
    <ReactModal isOpen={isOpen} onRequestClose={onClose} ariaHideApp={false} style={modalStyles}>
      <div className='d-flex align-items-start justify-content-between mb-3'>
        <div>
          <h5 className='fw-semibold mb-1'>Add email signature</h5>
          <p className='text-muted small mb-0'>
            Compose your email signature with rich text. It is appended to prefill invite emails and
            submission review emails.
          </p>
        </div>
        <button type='button' className='btn-close' onClick={onClose} />
      </div>

      {hasSignature && existingSignatureHtml ? (
        <div className='mb-3'>
          <label className='form-label small fw-medium text-muted'>Current signature preview</label>
          <div
            className='border rounded p-3 bg-light add-signature-modal__preview'
            dangerouslySetInnerHTML={{ __html: existingSignatureHtml }}
          />
        </div>
      ) : null}

      <div className='mb-3 add-signature-modal__editor'>
        <label className='form-label small fw-medium text-muted'>
          {hasSignature ? 'Edit signature' : 'Compose signature'}
        </label>
        <ReactQuill
          theme='snow'
          value={editorHtml}
          onChange={setEditorHtml}
          modules={modules}
          formats={formats}
          placeholder='Name, title, company, phone, website...'
        />
      </div>

      <div className='d-flex justify-content-end gap-2'>
        <button type='button' className='btn btn-light btn-sm px-3' onClick={onClose}>
          Cancel
        </button>
        <button
          type='button'
          className='btn btn-primary btn-sm px-4'
          onClick={handleSave}
          disabled={saving}
        >
          {saving ? 'Saving...' : 'Save signature'}
        </button>
      </div>
    </ReactModal>
  )
}

export default AddSignatureModal
